import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BiCalendar,
  BiCalendarPlus,
  BiHome,
  BiLinkExternal,
  BiBuilding,
  BiChat,
} from "react-icons/bi";
import { RiQuestionLine } from "react-icons/ri";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import * as yup from "yup";
import { useFormik } from "formik";
import { HashLoader } from "react-spinners";
import { GiMagnifyingGlass } from "react-icons/gi";

import { COLORS } from "../../utils/colors";
import { updateNavigation } from "../../store/mainReducer";
import { ROUTES } from "../../routing/routes";
import { MainButton } from "../MainButton";
import { sendEmail } from "../../api/user";

const feedbackValidationSchema = yup.object({
  feedbackMessage: yup
    .string("Enter feedback")
    .min(20, "Feedback should be of minimum 20 characters"),
  helpSubject: yup
    .string("Enter subject")
    .min(8, "Subject should be of minimum 20 characters"),
  helpMessage: yup
    .string("Enter message")
    .min(20, "Message should be of minimum 20 characters"),
});

export const BigMenu = ({ handleSnackbar }) => {
  let { userData } = useSelector((state) => state.main);
  const email = userData?.email;
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      feedbackMessage: "",
      helpSubject: "",
      helpMessage: "",
    },
    validationSchema: feedbackValidationSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);

      let message, subject;
      if (values.helpMessage && values.helpSubject) {
        message = values.helpMessage;
        subject = `Backoffice Help - ${values.helpSubject} - from ${email}`;
      } else {
        message = values.feedbackMessage;

        subject = `Backoffice Feedback - from ${email}`;
      }

      const { data, error } = await sendEmail({
        subject,
        message,
        to: "cristian@ooval.io",
        from: email,
      });

      if (error) {
        console.log(error);
        setLoading(false);
        handleSnackbar("Error sending email", "error");
        return;
      }

      if (data) {
        setSuccess(true);
      }

      setLoading(false);
      handleSnackbar("Email sent successfully", "success");
    } catch (error) {
      setLoading(false);
      handleSnackbar("Error sending email", "error");
      console.error(error);
    }
  };

  const handleClickOpen = (dialog) => {
    dialog === "help" && setOpenHelp(true);
    dialog === "feedback" && setOpen(true);
  };

  const handleClose = (dialog) => {
    dialog === "feedback" && setOpen(false);
    dialog === "help" && setOpenHelp(false);

    setLoading(false);
    setSuccess(false);
    formik.setValues({
      feedbackMessage: "",
      helpSubject: "",
      helpMessage: "",
    });
  };

  const goToLink = (link) => {
    window.open(link, "_blank");
  };

  const menuButtons = [
    {
      text: "Overview",
      icon: <BiHome size={20} />,
      navigation: "/",
    },
    {
      text: "Events",
      icon: <BiCalendar size={20} />,
      navigation: "/events",
    },
    {
      text: "Create Event",
      icon: <BiCalendarPlus size={20} />,
      navigation: "/create-event",
    },
    {
      text: "Organization",
      icon: <BiBuilding size={20} />,
      navigation: "/organization",
    },
    {
      text: "Logs",
      icon: <GiMagnifyingGlass size={20} />,
      navigation: "/logs",
    },
  ];

  const feedbackButtons = [
    {
      text: "Feedback",
      icon: <BiChat size={20} />,
      clickAction: () => handleClickOpen("feedback"),
    },
    {
      text: "Help",
      icon: <RiQuestionLine size={20} />,
      clickAction: () => handleClickOpen("help"),
    },
    {
      text: "Docs",
      icon: <BiLinkExternal size={20} />,
      clickAction: () => goToLink("https://ooval.readme.io"),
    },
  ];

  const activeColor = (navigation) => {
    if (window.location.pathname === navigation) {
      return COLORS.mainAccent;
    } else {
      return COLORS.black;
    }
  };

  const goTo = (navigation) => {
    dispatch(updateNavigation(navigation));

    switch (navigation) {
      case "/":
        navigate(ROUTES.DEFAULT);
        break;
      case "/events":
        navigate(ROUTES.EVENTS);
        break;
      case "/create-event":
        navigate(ROUTES.CREATE_EVENT);
        break;
      case "/organization":
        navigate(ROUTES.ORGANIZATION);
        break;
      case "/logs":
        navigate(ROUTES.LOGS);
        break;
      default:
        navigate(ROUTES.DEFAULT);
        break;
    }
  };

  return (
    <div className="big-menu-wrapper">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
          width: "-webkit-fill-available",
        }}
      >
        {menuButtons.map((button, index) => (
          <MainButton
            key={index}
            text={button.text}
            color={
              window.location.pathname === button.navigation
                ? COLORS.grey2
                : "transparent"
            }
            shadowColor={"transparent"}
            textColor={activeColor(button.navigation)}
            textHoverColor={activeColor(button.navigation)}
            hoverColor={COLORS.grey2}
            textAlignment={"left"}
            onClickAction={() => goTo(button.navigation)}
            fontSize={"16px"}
          >
            {button.icon}
          </MainButton>
        ))}
      </div>
      <div>
        {feedbackButtons.map((button, index) => (
          <MainButton
            key={index}
            text={button.text}
            color={"transparent"}
            shadowColor={"transparent"}
            textColor={COLORS.grey}
            textHoverColor={activeColor(button.navigation)}
            hoverColor={COLORS.grey2}
            textAlignment={"left"}
            onClickAction={button.clickAction}
            fontSize={"16px"}
          >
            {button.icon}
          </MainButton>
        ))}
      </div>
      <form>
        <Dialog open={open} onClose={() => handleClose("feedback")}>
          <DialogContent>
            <h2>
              <BiChat /> Feedback
            </h2>
            <p id="subheader" style={{ margin: "10px 0px 30px" }}>
              Anything you think we can do better? We would love to hear from
              you!
            </p>
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Summary of your feedback
            </p>
            <TextField
              fullWidth
              size="small"
              name="feedbackMessage"
              placeholder="I think you can improve the..."
              value={formik.values.feedbackMessage}
              onChange={formik.handleChange}
              error={
                formik.touched.feedbackMessage &&
                Boolean(formik.errors.feedbackMessage)
              }
              helperText={
                formik.touched.feedbackMessage && formik.errors.feedbackMessage
              }
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <MainButton
              disabled={loading || success}
              color={COLORS.black}
              shadowColor={"transparent"}
              textColor={COLORS.white}
              textHoverColor={COLORS.lightGrey}
              hoverColor={COLORS.black}
              textAlignment={"center"}
              onClickAction={() => formik.handleSubmit()}
            >
              {loading ? (
                <HashLoader color={COLORS.white} size={20} />
              ) : success ? (
                "Email sent"
              ) : (
                "Send email"
              )}
            </MainButton>
          </DialogActions>
        </Dialog>
        <Dialog open={openHelp} onClose={() => handleClose("help")}>
          <DialogContent>
            <h2>
              <RiQuestionLine /> How can we help?
            </h2>
            <p id="subheader" style={{ margin: "10px 0px 30px" }}>
              Have any question that you can't find the answer to? We are here
              to help!
            </p>
            <div style={{ height: "10px" }} />
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Subject of your message
            </p>
            <TextField
              fullWidth
              size="small"
              className="input"
              name="helpSubject"
              placeholder="Subject..."
              value={formik.values.helpSubject}
              onChange={formik.handleChange}
              error={
                formik.touched.helpSubject && Boolean(formik.errors.helpSubject)
              }
              helperText={
                formik.touched.helpSubject && formik.errors.helpSubject
              }
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
              }}
            />
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Summary of your question
            </p>
            <TextField
              fullWidth
              size="small"
              className="input"
              name="helpMessage"
              placeholder="I want to know how..."
              value={formik.values.helpMessage}
              onChange={formik.handleChange}
              error={
                formik.touched.helpMessage && Boolean(formik.errors.helpMessage)
              }
              helperText={
                formik.touched.helpMessage && formik.errors.helpMessage
              }
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <MainButton
              disabled={loading || success}
              color={COLORS.black}
              shadowColor={"transparent"}
              textColor={COLORS.white}
              textHoverColor={COLORS.lightGrey}
              hoverColor={COLORS.black}
              textAlignment={"center"}
              onClickAction={() => formik.handleSubmit()}
            >
              {loading ? (
                <HashLoader color={COLORS.white} size={20} />
              ) : success ? (
                "Email sent"
              ) : (
                "Send email"
              )}
            </MainButton>
          </DialogActions>
        </Dialog>
      </form>
    </div>
  );
};
